import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { FileText, Search, DollarSign, Percent, Calendar, GraduationCap, AlertCircle, CheckCircle, Save, Plus, Trash2, Edit, Eye, ExternalLink, Zap } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

export default function FinancialPlans() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [studentPlan, setStudentPlan] = useState(null);
  const [parcelas, setParcelas] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [coursePlans, setCoursePlans] = useState([]);

  const meses = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = students.filter(student => 
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.registration_number?.includes(searchTerm) ||
        student.class_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, plansRes] = await Promise.all([
        api.get('/students'),
        api.get('/course-financial-plans')
      ]);
      const activeStudents = (studentsRes.data || []).filter(s => s.active !== false);
      setStudents(activeStudents);
      setFilteredStudents(activeStudents);
      setCoursePlans(plansRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setDialogOpen(true);
    
    try {
      // Buscar plano financeiro do aluno
      const planRes = await api.get(`/students/${student.id}/financial-plan`);
      setStudentPlan(planRes.data);
      
      // Gerar/buscar parcelas do ano - passar também os planos de curso do aluno
      await loadParcelas(student.id, planRes.data, student.financial_plans);
    } catch (error) {
      setStudentPlan(null);
      setParcelas([]);
    }
  };

  const loadParcelas = async (studentId, plan, financialPlanIds = []) => {
    try {
      // Calcular valor mensal baseado nos planos de curso selecionados
      let valorMensalTotal = 0;
      let descontoTotal = 0;
      let diaVencimento = plan?.due_day || 10;
      let planosNomes = [];
      
      // Se tiver planos de curso selecionados, calcular o valor mensal e coletar nomes
      if (financialPlanIds && financialPlanIds.length > 0) {
        financialPlanIds.forEach(planId => {
          const coursePlan = coursePlans.find(p => p.id === planId);
          if (coursePlan) {
            valorMensalTotal += coursePlan.installment_value || 0;
            planosNomes.push({
              id: coursePlan.id,
              name: coursePlan.name,
              category: coursePlan.category,
              value: coursePlan.installment_value || 0
            });
            // Se o plano tiver desconto habilitado
            if (coursePlan.discount_enabled && coursePlan.discount_percent > 0) {
              descontoTotal += (coursePlan.installment_value || 0) * (coursePlan.discount_percent / 100);
            }
          }
        });
      } else if (plan?.monthly_value > 0) {
        // Usar plano financeiro individual se não tiver planos de curso
        valorMensalTotal = plan.monthly_value;
        descontoTotal = plan.discount_value || (valorMensalTotal * (plan.discount_percentage || 0) / 100);
        planosNomes.push({
          id: null,
          name: 'Mensalidade',
          category: 'mensalidade',
          value: valorMensalTotal
        });
      }
      
      // Criar descrição baseada nos planos
      const descricaoPlanos = planosNomes.length > 0 
        ? planosNomes.map(p => p.name).join(' + ')
        : 'Mensalidade';
      
      const res = await api.get(`/titles?student_id=${studentId}`);
      const titulos = res.data || [];
      
      const parcelasGeradas = meses.map(mes => {
        const referencia = `${anoSelecionado}-${mes.value}`;
        const tituloExistente = titulos.find(t => 
          t.reference_month === referencia || 
          (t.due_date && t.due_date.startsWith(referencia))
        );
        
        if (tituloExistente) {
          // Usar paid_value quando pago, senão valor com desconto
          const valorFinal = tituloExistente.status === 'pago' 
            ? (tituloExistente.paid_value || tituloExistente.original_value - (tituloExistente.discount_value || 0))
            : tituloExistente.original_value - (tituloExistente.discount_value || 0);
          
          return {
            mes: mes.value,
            mesNome: mes.label,
            referencia,
            descricao: tituloExistente.description || descricaoPlanos,
            planos: planosNomes,
            valor_original: tituloExistente.original_value || 0,
            desconto: tituloExistente.discount_value || 0,
            valor_final: valorFinal,
            valor_pago: tituloExistente.paid_value,
            vencimento: tituloExistente.due_date || '',
            status: tituloExistente.status,
            titulo_id: tituloExistente.id,
            existente: true,
            payment_method: tituloExistente.payment_method
          };
        } else {
          // Usar valores calculados dos planos de curso
          return {
            mes: mes.value,
            mesNome: mes.label,
            referencia,
            descricao: `${descricaoPlanos} - ${mes.label}/${anoSelecionado}`,
            planos: planosNomes,
            valor_original: valorMensalTotal,
            desconto: descontoTotal,
            valor_final: valorMensalTotal - descontoTotal,
            valor_pago: null,
            vencimento: `${anoSelecionado}-${mes.value}-${String(diaVencimento).padStart(2, '0')}`,
            status: null,
            titulo_id: null,
            existente: false,
            payment_method: null
          };
        }
      });
      
      setParcelas(parcelasGeradas);
    } catch (error) {
      console.error('Erro ao carregar parcelas:', error);
      setParcelas([]);
    }
  };

  const handleParcelaChange = (index, field, value) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index] = { ...novasParcelas[index], [field]: value };
    
    // Recalcular valor final
    if (field === 'valor_original' || field === 'desconto') {
      const original = field === 'valor_original' ? parseFloat(value) || 0 : parseFloat(novasParcelas[index].valor_original) || 0;
      const desc = field === 'desconto' ? parseFloat(value) || 0 : parseFloat(novasParcelas[index].desconto) || 0;
      novasParcelas[index].valor_final = original - desc;
    }
    
    setParcelas(novasParcelas);
  };

  const handleSaveParcelaIndividual = async (parcela, index) => {
    setSaving(true);
    try {
      if (parcela.existente && parcela.titulo_id) {
        // Atualizar título existente
        await api.put(`/titles/${parcela.titulo_id}`, {
          original_value: parseFloat(parcela.valor_original) || 0,
          discount_value: parseFloat(parcela.desconto) || 0,
          due_date: parcela.vencimento,
          description: parcela.descricao
        });
        toast.success(`Parcela de ${parcela.mesNome} atualizada!`);
      } else {
        // Criar novo título
        await api.post('/titles', {
          student_id: selectedStudent.id,
          reference_month: parcela.referencia,
          original_value: parseFloat(parcela.valor_original) || 0,
          discount_value: parseFloat(parcela.desconto) || 0,
          due_date: parcela.vencimento,
          description: parcela.descricao || `Mensalidade ${parcela.mesNome}/${anoSelecionado}`
        });
        
        const novasParcelas = [...parcelas];
        novasParcelas[index].existente = true;
        novasParcelas[index].status = 'pendente';
        setParcelas(novasParcelas);
        
        toast.success(`Parcela de ${parcela.mesNome} criada!`);
      }
    } catch (error) {
      toast.error('Erro ao salvar parcela');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParcela = async (parcela, index) => {
    if (!parcela.titulo_id) return;
    if (parcela.status === 'pago') {
      toast.error('Não é possível excluir uma parcela já paga');
      return;
    }
    
    if (!window.confirm(`Excluir a parcela de ${parcela.mesNome}?`)) return;
    
    try {
      await api.delete(`/titles/${parcela.titulo_id}`);
      
      const novasParcelas = [...parcelas];
      novasParcelas[index].existente = false;
      novasParcelas[index].titulo_id = null;
      novasParcelas[index].status = null;
      setParcelas(novasParcelas);
      
      toast.success('Parcela excluída!');
    } catch (error) {
      toast.error('Erro ao excluir parcela');
    }
  };

  const handleGerarTodasParcelas = async () => {
    if (!window.confirm(`Gerar/atualizar todas as parcelas de ${anoSelecionado}?`)) return;
    
    setSaving(true);
    let sucesso = 0;
    let erros = 0;
    
    for (let i = 0; i < parcelas.length; i++) {
      const parcela = parcelas[i];
      if (!parcela.valor_original || parcela.valor_original <= 0) continue;
      if (parcela.status === 'pago') continue; // Não alterar parcelas pagas
      
      try {
        if (parcela.existente && parcela.titulo_id) {
          await api.put(`/titles/${parcela.titulo_id}`, {
            original_value: parseFloat(parcela.valor_original) || 0,
            discount_value: parseFloat(parcela.desconto) || 0,
            due_date: parcela.vencimento,
            description: parcela.descricao
          });
        } else {
          await api.post('/titles', {
            student_id: selectedStudent.id,
            reference_month: parcela.referencia,
            original_value: parseFloat(parcela.valor_original) || 0,
            discount_value: parseFloat(parcela.desconto) || 0,
            due_date: parcela.vencimento,
            description: parcela.descricao || `Mensalidade ${parcela.mesNome}/${anoSelecionado}`
          });
        }
        sucesso++;
      } catch (error) {
        erros++;
      }
    }
    
    setSaving(false);
    
    if (sucesso > 0) {
      toast.success(`${sucesso} parcelas processadas com sucesso!`);
      await loadParcelas(selectedStudent.id, studentPlan, selectedStudent.financial_plans);
    }
    if (erros > 0) {
      toast.error(`${erros} parcelas com erro`);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pago': return <Badge className="bg-green-500">Pago</Badge>;
      case 'pendente': return <Badge className="bg-amber-500">Pendente</Badge>;
      case 'vencido': return <Badge className="bg-red-500">Vencido</Badge>;
      default: return <Badge variant="outline">Não gerado</Badge>;
    }
  };

  // Calcular totais do plano do aluno
  const getStudentPlanSummary = (student) => {
    const plans = student.financial_plans || [];
    if (plans.length === 0) return null;
    
    let totalMensal = 0;
    let totalAnual = 0;
    const planDetails = [];
    
    plans.forEach(planId => {
      const plan = coursePlans.find(p => p.id === planId);
      if (plan) {
        totalMensal += plan.installment_value || 0;
        totalAnual += (plan.installment_value || 0) * (plan.num_installments || 12);
        planDetails.push(plan);
      }
    });
    
    return { totalMensal, totalAnual, planDetails, count: plans.length };
  };

  const navigateToStudentEdit = () => {
    setDialogOpen(false);
    navigate(`/students?edit=${selectedStudent.id}`);
  };

  return (
    <div className="p-6 space-y-6 notranslate" data-testid="financial-plans-page" translate="no">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Planos Financeiros
          </h1>
          <p className="text-muted-foreground mt-1">Visualize e gerencie os planos financeiros dos alunos</p>
        </div>
        <Button 
          onClick={() => navigate('/v2/financeiro/gerar-titulos')}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Zap className="w-4 h-4 mr-2" />
          Gerar Títulos DNA Pay
        </Button>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Aluno</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Buscar por nome, matrícula ou turma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alunos */}
      <Card>
        <CardHeader>
          <CardTitle>Alunos</CardTitle>
          <CardDescription>
            {filteredStudents.length} alunos encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : filteredStudents.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum aluno encontrado com os critérios de busca.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Matrícula</TableHead>
                    <TableHead>Turma</TableHead>
                    <TableHead>Planos</TableHead>
                    <TableHead>Valor Mensal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.slice(0, 50).map((student) => {
                    const planSummary = getStudentPlanSummary(student);
                    return (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {student.registration_number}
                        </TableCell>
                        <TableCell>{student.class_name}</TableCell>
                        <TableCell>
                          {planSummary ? (
                            <Badge variant="outline">{planSummary.count} plano(s)</Badge>
                          ) : (
                            <Badge variant="secondary">Sem plano</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {planSummary ? (
                            <span className="font-medium text-green-600">
                              {formatCurrency(planSummary.totalMensal)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={student.active !== false ? 'default' : 'secondary'}>
                            {student.active !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelectStudent(student)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {filteredStudents.length > 50 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Mostrando 50 de {filteredStudents.length} alunos.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog do Plano Financeiro */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              Plano Financeiro - {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedStudent?.class_name} | Matrícula: {selectedStudent?.registration_number}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="resumo" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="resumo">Resumo do Plano</TabsTrigger>
              <TabsTrigger value="parcelas">Parcelas {anoSelecionado}</TabsTrigger>
            </TabsList>

            {/* Tab Resumo */}
            <TabsContent value="resumo" className="space-y-4 mt-4">
              {/* Resumo do Plano Configurado */}
              {studentPlan && (studentPlan.monthly_value > 0 || selectedStudent?.financial_plans?.length > 0) ? (
                <div className="space-y-4">
                  {/* Planos do Curso */}
                  {selectedStudent?.financial_plans?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-green-600" />
                          Planos Contratados
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {selectedStudent.financial_plans.map(planId => {
                            const plan = coursePlans.find(p => p.id === planId);
                            if (!plan) return null;
                            return (
                              <div key={planId} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                                <div>
                                  <p className="font-medium">{plan.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {plan.num_installments}x de {formatCurrency(plan.installment_value)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">
                                    {formatCurrency(plan.installment_value * plan.num_installments)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Total</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Configuração Individual */}
                  {studentPlan.monthly_value > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Configuração Individual</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">Mensalidade</p>
                            <p className="text-xl font-bold">{formatCurrency(studentPlan.monthly_value)}</p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">Desconto</p>
                            <p className="text-xl font-bold text-green-600">
                              {studentPlan.discount_percentage > 0 
                                ? `${studentPlan.discount_percentage}%`
                                : formatCurrency(studentPlan.discount_value || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">Valor Final</p>
                            <p className="text-xl font-bold text-blue-600">
                              {formatCurrency(studentPlan.final_value || studentPlan.monthly_value - (studentPlan.discount_value || 0))}
                            </p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">Vencimento</p>
                            <p className="text-xl font-bold">Dia {studentPlan.due_day || 10}</p>
                          </div>
                        </div>
                        {studentPlan.scholarship_type && (
                          <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-sm text-purple-700">
                              <strong>Bolsa:</strong> {studentPlan.scholarship_type}
                            </p>
                          </div>
                        )}
                        {studentPlan.notes && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              <strong>Observações:</strong> {studentPlan.notes}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Botão para editar no cadastro */}
                  <div className="flex justify-center pt-4">
                    <Button onClick={navigateToStudentEdit} variant="outline" className="gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Editar Plano no Cadastro do Aluno
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Este aluno não possui um plano financeiro configurado.
                    <Button 
                      variant="link" 
                      className="p-0 h-auto ml-2"
                      onClick={navigateToStudentEdit}
                    >
                      Configurar no cadastro do aluno
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Tab Parcelas */}
            <TabsContent value="parcelas" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Ano:</Label>
                  <Select value={anoSelecionado.toString()} onValueChange={(v) => {
                    setAnoSelecionado(parseInt(v));
                    if (selectedStudent) loadParcelas(selectedStudent.id, studentPlan, selectedStudent.financial_plans);
                  }}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(ano => (
                        <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {canEdit() && (
                  <Button onClick={handleGerarTodasParcelas} disabled={saving} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Gerar/Atualizar Todas
                  </Button>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-24">Mês</TableHead>
                      <TableHead>Valor (R$)</TableHead>
                      <TableHead>Desconto (R$)</TableHead>
                      <TableHead>Valor Final</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Valor Pago</TableHead>
                      {canEdit() && <TableHead className="w-32">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parcelas.map((parcela, index) => (
                      <TableRow key={parcela.mes} className={parcela.status === 'pago' ? 'bg-green-50' : ''}>
                        <TableCell className="font-medium">{parcela.mesNome}</TableCell>
                        <TableCell>
                          {canEdit() && parcela.status !== 'pago' ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={parcela.valor_original}
                              onChange={(e) => handleParcelaChange(index, 'valor_original', e.target.value)}
                              className="w-28"
                            />
                          ) : (
                            formatCurrency(parcela.valor_original)
                          )}
                        </TableCell>
                        <TableCell>
                          {canEdit() && parcela.status !== 'pago' ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={parcela.desconto}
                              onChange={(e) => handleParcelaChange(index, 'desconto', e.target.value)}
                              className="w-28"
                            />
                          ) : (
                            <span className="text-green-600">-{formatCurrency(parcela.desconto)}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(parcela.valor_final)}
                        </TableCell>
                        <TableCell>
                          {canEdit() && parcela.status !== 'pago' ? (
                            <Input
                              type="date"
                              value={parcela.vencimento?.split('T')[0] || ''}
                              onChange={(e) => handleParcelaChange(index, 'vencimento', e.target.value)}
                              className="w-36"
                            />
                          ) : (
                            formatDate(parcela.vencimento)
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(parcela.status)}
                        </TableCell>
                        <TableCell>
                          {parcela.status === 'pago' ? (
                            <span className="font-bold text-green-600">
                              {formatCurrency(parcela.valor_pago || parcela.valor_final)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        {canEdit() && (
                          <TableCell>
                            <div className="flex gap-1">
                              {parcela.status !== 'pago' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSaveParcelaIndividual(parcela, index)}
                                  disabled={saving}
                                  title={parcela.existente ? "Atualizar" : "Criar"}
                                >
                                  <Save className="w-4 h-4" />
                                </Button>
                              )}
                              {parcela.existente && parcela.status !== 'pago' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600"
                                  onClick={() => handleDeleteParcela(parcela, index)}
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totais */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Bruto</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(parcelas.reduce((sum, p) => sum + (parseFloat(p.valor_original) || 0), 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Descontos</p>
                      <p className="text-lg font-bold text-green-600">
                        -{formatCurrency(parcelas.reduce((sum, p) => sum + (parseFloat(p.desconto) || 0), 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total a Pagar</p>
                      <p className="text-lg font-bold text-blue-600">
                        {formatCurrency(parcelas.reduce((sum, p) => sum + (parseFloat(p.valor_final) || 0), 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Pago</p>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(parcelas.filter(p => p.status === 'pago').reduce((sum, p) => sum + (parseFloat(p.valor_pago) || parseFloat(p.valor_final) || 0), 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
